/*
 * Copyright (c)  2020 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosInstance} from 'axios';
import {ApplicationContext} from 'context/ApplicationContext';
import isArray from 'lodash.isarray';
import isEmpty from 'lodash.isempty';
import isFunction from 'lodash.isfunction';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import isNumber from 'lodash.isnumber';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import partition from 'lodash.partition';
import {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {get, getApplicationContext} from '../environment/environment';
import {error} from '../logger/logger';
import {
  Asset,
  CarrierDeviceIdentifierType,
  Crashboxx,
  CTCEntity,
  DownloadJobType,
  Entity,
  GenericObject,
  GeoFenceAttributes,
  HVSRecord,
  Link,
  PlatformDeviceTypes,
  PlatformFileType,
  Role,
  User,
  VideoEvent
} from '../types/entities';
import {
  PartitionBulkUpdates,
  SearchTerm
} from '../types/general';
import {
  LoginResult,
  MfaEnrollmentFactor,
  MfaStatus,
  TOTP_FACTOR_TYPE
} from '../types/mfa';
import {ResolveHistoryParams} from '../types/reports';
import {
  AxiosConfig,
  ConnectConfig,
  DeviceCommunicationSearchTypes,
  DeviceSearchTypes,
  IONVisionSearchParams,
  LoadDeviceFilesParams,
  MAX_RESULTS,
  Service,
  ServiceError
} from '../types/services';
import {ImportAccount} from '../types/templates';
import {fromCSV} from '../utils/CSVUtils';
import {convertModularSearchDateRange, getDaysFromToday} from '../utils/DateUtil';
import {safeJSONParse, safeJSONStringify} from '../utils/JSONUtils';
import {onlyNumbers} from '../utils/NumberUtil';
import {idMap, removeUndefined} from '../utils/ObjectUtil';
import {contains, normalizeClauseList, processDelimiters} from '../utils/StringUtil';
import {createAsyncClient} from './AsyncClientManager';
import {
  getCodeMappings,
  getErrorCode,
  hasDiagCode
} from './JobMetaDefinitions';
import {errorInterceptor} from './ServiceErrors';

export const PAGE_LIMIT = 99;
export const MAX_LIMIT = 10000;

axios.defaults.withCredentials = true;

const entityResourceURI = {
  [Entity.ioprofile]           : 'ioProfile',
  [Entity.deviceconfiguration] : 'deviceConfiguration'
};

/**
 * Defines a reference to the configured Axios HTTP client instance.
 */
export let client: AxiosInstance;

/**
 * Defines a reference to the CTC Configuration which is used by all requests.
 */
export let config: AxiosConfig;

/**
 * Provides a convenience function which allows for changing the default credentials
 * mode for both the connect axios client as well as the global axios instance.
 *
 * Note, this function should only ever be invoked when a request must be made which
 * explicitly omits credentials from being including within the headers, such as when
 * invoking the forgot password, or reset password endpoints.
 */
export const resetCredentials = (enable = true, api: AxiosInstance = null) => {
  if ((client || api)?.defaults) {
    client.defaults.withCredentials = enable;
  }
  if (axios?.defaults) {
    axios.defaults.withCredentials  = enable;
  }
};

/**
 * Provides a utility which resolves auxAppKey to fallback based on the application
 * context; i.e. 'ion', 'assistcargo', etc..
 *
 * As the number of reseller's grow, auxillary application keys will be added for
 * each reseller and available via an application's environment.json document via
 * the "auxAppKeys" entry.
 *
 * ```typescript
 *   // environment.json
 *   {
 *     appKey     : '82c904bd-71a8-4d38-a645-e6980510b7e2',
 *     auxAppKey  : 'e7d961ff-5943-4a2a-9f95-bd8598256d25',
 *     auxAppKeys : {
 *       suite       : 'e7d961ff-5943-4a2a-9f95-bd8598256d25',
 *       assistcargo : '4ff58490-2c6a-434a-9479-c49340c4a9d6'
 *     }
 *   };
 *
 *   import {getAuxAppKeyForContext} from '@ion/core/src/services';

 *   logger.info(getAuxAppKeyForContext(ApplicationContext.ion));
 *   // 'e7d961ff-5943-4a2a-9f95-bd8598256d25'
 *
 *   logger.info(getAuxAppKeyForContext(ApplicationContext.assistcargo));
 *   // '4ff58490-2c6a-434a-9479-c49340c4a9d6'
 * ```
 *
 * @see     ApplicationContext
 * @param   context an optional `ApplicationContext` from which to retrieve it's
 *          associated application key. If not provided, the context defaults to
 *          ApplicationContext.ion.
 * @returns The CTC application key for the given context, defaults to the CTC
 *          application key for `ApplicationContext.ion` if the `context` is unable
 *          to be established.
 */
export const getAuxAppKeyForContext = (context?: ApplicationContext): string => {
  const auxKeys = get('auxAppKeys') || {};
  return auxKeys[context || getApplicationContext()];
};

/**
 * Provides a utility which resolves a potentially nil appKey to fallback to using
 * either the "auxAppKey", followed by "appKey", as defined in the application's
 * environment.json document.
 */
export const resolveAppKey = (appKey?: string): string | undefined => {
  return appKey || getAuxAppKeyForContext() || get('appKey');
};

/**
 * Given an array of results services entries, and an array of CTC Tool Assets,
 * compares the results services tag report and augments the assets with a
 * tagReport property of the tool is reporting.
 */
export const resolveAssetTools = async(report, assets: Asset[]): Promise<any> => {
  const {results} = report ?? {};
  if (results?.length) {
    const {lmuGatewayEvent:event} = results[0];
    if (event?.tagRecords) {
      const map = idMap(assets);
      const verified = {};
      event.tagRecords.forEach(item => {
        const record = item.tagReport || item;
        const toolId = entityId(record.asset);
        const asset  = map[toolId];
        if (asset) {
          asset.tagReport = record;
        }
        verified[toolId] = true;
      });
      assets.forEach(asset => {
        const valid = verified[asset.assetId || asset.id];
        if (!valid && asset.tagReport) {
          delete asset.tagReport;
        }
      });
    } else if (assets?.length) {
      assets.forEach(asset => {
        delete asset.tagReport;
      });
    }
  }
  return assets;
};

/**
 * Given a list of iOn Vision Video Events, and a list of HVS Records, augments
 * the HVSRecords with an iOnVisionEvent property which references the iOn Vision
 * Event which corresponds to the given HVS Record (if any).
 */
export const resolveIOnVisionEvents = (events: VideoEvent[], hvs: HVSRecord[]): HVSRecord[] => {
  const ids = idMap(events, 'avlEventUuid');
  return hvs.map((item) => ({
    ...item,
    iOnVisionEvent : ids[item.messageUuid] || null
  }));
};

export const resolveGeofenceEvents = (events: GeoFenceAttributes[], hvs: HVSRecord[]): HVSRecord[] => {
  const ids = idMap(events, 'messageUuid');
  return hvs.map((item) => {
    return {
      ...item,
      geofenceType : ids[item.messageUuid]?.geofenceType || null
    };
  });
};

export const resolveCrashboxxEvents = (events: Crashboxx[], hvs: HVSRecord[]): HVSRecord[] => {
  const ids = idMap(hvs, 'assetId');
  const filteredCrashboxxEvents = [];
  if (events?.length && hvs?.length) {
    events.forEach((crashboxxEvent) => {
      const match = ids[crashboxxEvent.assetId];
      match && filteredCrashboxxEvents.push(crashboxxEvent);
    });
  }
  return [...hvs, ...filteredCrashboxxEvents];
};

export const resolveHistoryEvents = ({
  history,
  crashboxxEvents,
  ionVisionEvents,
  geofenceEvents
}: ResolveHistoryParams): HVSRecord[] => {
  const idsGeofence = idMap(geofenceEvents, 'messageUuid');
  const idsVision = idMap(ionVisionEvents, 'avlEventUuid');
  const idsCrashboxx = idMap(crashboxxEvents, 'assetId');
  const resolvedCrashboxxEvents = new Set<HVSRecord[]>();
  const resolvedGeofenceEvents = new Set<HVSRecord[]>();

  const augmentedHistory = history.map((item) => {
    const crashboxx = idsCrashboxx[item?.id];
    const geofence = idsGeofence[item?.messageUuid];

    if (crashboxx) {
      resolvedCrashboxxEvents.add(crashboxx);
    }
    if (geofence) {
      resolvedGeofenceEvents.add(geofence);
    }
    return {
      ...item,
      iOnVisionEvent : idsVision[item.messageUuid] || null
    };
  });
  const geofenceValues = resolvedGeofenceEvents.values();
  const crashboxxValues = resolvedCrashboxxEvents.values();
  return [...augmentedHistory, ...crashboxxValues, ...geofenceValues];
};

/**
 * Given a User and list of roles as HATEOAS links, inspects the users roles against
 * the provided list of roles and returns the full entity representation of the role.
 * This utility essentially allows for converting a role link to a role entity.
 *
 * @param  user The User entity for which their application role from their list
 *         of Role links is to be returned.
 * @param  roles The fully qualified role entities for which the user's matching
 *         role link is to be used to determine which role entity they are assigned.
 */
export const resolveRole = (user: User, roles: Role[]): any => {
  const ids = {};
  user.roles.forEach(role => {
    ids[entityId(role.href)] = true;
  });
  return roles.find(role => ids[role.id]);
};

/**
 * Given an IONVisionSearchParams instance, returns a search object suitable
 * for use with the underlying search request.
 */
export const normalizeIONVisionSearchParams = ({
  createdOn,
  appmsgEventTime,
  avlEventTime,
  groups,
  accountId,
  deviceId,
  avlEventUuid,
  appMessageUuid,
  id,
  ctcDeviceId
}: IONVisionSearchParams): any => {
  return removeUndefined({
    createdOn,
    appmsgEventTime : appmsgEventTime?.toISOString(),
    avlEventTime,
    accountId,
    deviceId,
    groups,
    appMessageUuid,
    avlEventUuid,
    id,
    ctcDeviceId
  });
};

/**
 * Given an object which contains Date value properties, converts the dates to
 * their ISO string representations.
 */
export const normalizeDates = (params: any): any => {
  Object.keys(params).forEach(prop => {
    const val = params?.[prop];
    if (val?.toISOString) {
      params[prop] = val.toISOString();
    }
  });
  return params;
};

/**
 * @function parseExtendedAttributes
 *
 * Provides a utility which allows for retrieving and / or parsing a given
 * entity's extendedAttribute.
 *
 * @param  {object} entity A CTC entity for which to return extendedAttributes.
 * @param  {boolean} valueOnly When true, returns the parsed extendedAttribute
 *         value property;
 * @return {object} the entity extendedAttributes, or, the parsed entity
 *         extendedAttributes.value if valueOnly argument is true.
 */
export const parseExtendedAttributes = (entity: any, valueOnly = false) => {
  const {extendedAttribute} = entity;
  if (extendedAttribute) {
    if (valueOnly) {
      if (!extendedAttribute.value) {
        return null;
      }
      const result = safeJSONParse(extendedAttribute.value);
      return result || extendedAttribute.value || null;
    }
    return extendedAttribute;
  }
  return null;
};

/**
 * @function getExtendedAttributeValue
 *
 * Returns the value of the given property from the entity's extended attribute
 * value property.
 *
 * ```typescript
 *
 *   import {getExtendedAttributeValue} from 'core/services/Connect';
 *
 *   const entity = {
 *     extendedAttribute: {
 *       application: {
 *         ...
 *       },
 *       value: '{"someValue": "Some Value", "someOtherValue": "Some Other Value"}'
 *     }
 *   };
 *
 *   console.log(getExtendedAttributeValue(entity, 'someValue'));
 *   // "Some Value"
 *
 *   console.log(getExtendedAttributeValue(entity, 'someOtherValue'));
 *   // "Some Other Value"
 *
 * ```
 *
 * @method getExtendedAttributeValue
 * @param  {object} entity A CTC entity for which to return extendedAttribute
 *         value of the given property.
 * @param  {string} property The property for which the value is to be returned.
 * @return {*any} the value of the property argument.
 */
export const getExtendedAttributeValue = (entity: any, property: string): any => {
  const extendedAttribute = parseExtendedAttributes(entity, true);
  return extendedAttribute ? extendedAttribute[property] : undefined;
};

/**
 * @function setHierLevel
 *
 * Given a connect service url, returns url with or without the hierLevel parameter
 * included based on the include argument (when true, hierLevel will be appended to
 * the url, otherwise, when false, the url will be returned unchanged).
 *
 * ```typescript
 *   import {setHierLevel} from 'core/services/Connect';
 *
 *   console.log(setHierLevel('/assets', true));
 *   // /assets?hierLevel=ALL
 *
 *   console.log(setHierLevel('/assets?page=1&pageSize=50', true));
 *   // /assets?page=1&pageSize=50&hierLevel=ALL
 *
 *   console.log(setHierLevel('/assets?page=1&pageSize=50'));
 *   // /assets?page=1&pageSize=50
 *
 * ```
 *
 * @method  setHierLevel
 * @param   {string} url The string to be used to concatenate depending on the value
 *          of the include parameter.
 * @param   {boolean} include When true, indicates that hierLevel is to be appended
 *          to the url (in addition to any existing parameters)
 */
export const setHierLevel = (url: string, include: boolean): string => {
  const token = !contains(url, '?') ? '?' : '&';
  return `${url}${include ? `${token}hierLevel=ALL` : ''}`;
};

/**
 * @function url
 *
 * Provides a utility function which is used to generate a request service
 * url based on the provided configurations root url.
 *
 * ```typescript
 *
 *    export const getRole = async(id: number): Promise<any> => {
 *      // use the default connect services ...
 *      const response = await client.get(url(`/roles/${id}`));
 *      return parse(response, 'role');
 *    }
 *
 * ```
 */
export const url = (uri: string, service = Service.Connect): string => {
  const baseURL = config?.[service] || '';
  return `${baseURL}${uri}`;
};

/**
 * Provides a convenience method for parsing the id of a given Connect Entity.
 * Connect entities, by design, are based on HATEOAS, see:
 * @see https://en.wikipedia.org/wiki/HATEOAS
 *
 * Each entity contains "links" to associated entities, whereby the links provide
 * an 'href' property representing the URI to the entity which contains the entity
 * id as the ending URI part. This method, as the name suggests, parses the entity
 * id from either the id or the entity's href.
 *
 * ```typescript
 *
 *   import {entityId} from '@ion/core/src/services/ConnectUtils';
 *
 *   // returns the entity id directly, or parsed from href ...
 *   const id = entityId(entity);
 *   entityId({id: 123, ...}) // 123
 *   entityId('https://connect.calamp.com/some/entity/id/456') // 456
 *   entityId({href: 'https://connect.calamp.com/some/entity/id/321'}) // 321
 *
 * ```
 *
 * @function entityId
 * @param    {object|string} entity Either a CTC Entity, a CTC HATEAOS link object,
 *           or a CTC HATEAOS href link from which the entity id is to be parsed
 *           and returned.
 * @return   {number} the id of the given entity argument.
 */
export const entityId = (entity: any, parseNumber = true): any => {
  let result = entity?.href || entity;
  if (isString(result)) {
    result = result.substring(result.lastIndexOf('/') + 1);
    if (parseNumber) {
      const parsed = parseInt(result, 10);
      return isNaN(parsed) ? result : parsed;
    }
    return result;
  } else {
    result = (result?.id || result);
  }
  return isNumber(result) ? result : undefined;
};

/**
 * Given a collection of entities, will resolve and return a collection containing
 * each entity's id.
 *
 * ```typescript
 *
 *   import {entityIds} from '@ion/core/src/services/Connect';
 *
 *   // returns the entity id directly, or parsed from href ...
 *   const entities = [
 *     {_self: {href: 'https://connect.calamp.com/services/assets/456}, ...},
 *     {id: 123, ...}
 *   ]
 *   entityIds(entities) // [456, 123]
 *
 * ```
 */
export const entityIds = (entities: any[]): number[] => {
  return entities?.map(entity => entityId(entity));
};

/**
 * Provides a simple utility for determining if two entities belong to the
 * same account.
 */
export const hasSameAccount = (a: CTCEntity, b: CTCEntity): boolean => {
  return entityId(a?.account) === entityId(b?.account);
};

/**
 * Decoupling the logic for transforming endpoints from the audit function
 */
export const getEntityResourceURI = (entity: Entity): string => {
  return entityResourceURI[entity] || entity;
};

/**
 * Provides a utility which determines the name to be used for resolving
 * individual bulk operation results.
 */
export const getBulkOperationName = (item: CTCEntity, name: any = 'name'): string => {
  if (isFunction(name)) {
    const generator = name as Function;
    return generator(item);
  }
  return item[name];
};

/**
 * Provides a utility for updating an entity's "rel" attribute.
 * This use-case is commonly required when associating entities,
 * for example, an account to a device, an device to an asset,
 * and so forth, whereas, for example, in the case of a device
 * the _self rel (device._self.rel) will be "self", but when
 * associating the device to an asset, the rel will need to be
 * changed to "device".
 *
 * ```typescript
 *
 *   import {setRel} from '@ion/core/src/services/ConnectUtils';
 *
 *   // returns an new version of the entity with the _self.rel
 *   // attribute changed to "device" ...
 *   const updatedEntity = setRel(entity, 'device');
 *
 *   // returns an new version of the entity's "_self" attribute
 *   // with the "rel" attribute changed to "device" ...
 *   const updatedEntitySelf = setRel(entity._self, 'device');
 *
 * ```
 */
export const setRel = (entity: CTCEntity, rel: string) => {
  return Object.assign({}, entity?._self || entity, {rel});
};

/**
 * @function parse
 *
 * The parseEntity utility allows for parsing a CTC response which returns
 * an Array containing a single entity.
 *
 * ```typescript
 *
 *    export const getAccount = async(id: number): Promise<any> => {
 *      const response = await client.get(url(`/account/${id}`));
 *      return parse(response, 'account');
 *    }
 *
 * ```
 */
export const parse = (resp: any, entity?: string): object => {
  const results = resp?.data?.response?.results;
  if (results?.length) {
    const result = results[0];
    if (!entity) {
      const attrs = Object.keys(result);
      if (attrs.length === 1) {
        entity = attrs[0];
      }
    }
    return entity ? result[entity] : result;
  }
};

/**
 * @function readIntermediateToken
 *
 * Reads the single-use MFA intermediate token from a Connect response, preferring
 * the `X-Intermediate-Token` response header (axios lower-cases header keys) and
 * falling back to a body field when Cross-Origin Resource Sharing (CORS) does not
 * expose the header.
 *
 * ```typescript
 *    import {readIntermediateToken} from '@ion/core/src/services/ConnectUtils';
 *    ...
 *    const token = readIntermediateToken(response);
 * ```
 */
export const readIntermediateToken = (resp: any): string => (
  resp?.headers?.['x-intermediate-token'] ||
  resp?.data?.response?.results?.[0]?.intermediateToken || ''
);

/**
 * @function readRotatedIntermediateToken
 *
 * Extracts a rotated MFA intermediate token from either a successful Connect
 * response or a thrown `ServiceError`, delegating the header/body parsing to
 * `readIntermediateToken`. A thrown `ServiceError` carries the axios response
 * under `response`; a successful response has no top-level `response`, so it is
 * passed through unchanged.
 *
 * ```typescript
 *    import {readRotatedIntermediateToken} from '@ion/core/src/services/ConnectUtils';
 *    ...
 *    const token = readRotatedIntermediateToken(serviceError);
 * ```
 */
export const readRotatedIntermediateToken = (source): string => (
  readIntermediateToken(source?.response || source)
);

/**
 * @function filterTotpFactors
 *
 * Filters an enrollment factor catalog down to the supported Time-based One-time
 * Password (TOTP) factors only. Unsupported factors (for example push) are removed
 * so they are never rendered.
 *
 * ```typescript
 *    import {filterTotpFactors} from '@ion/core/src/services/ConnectUtils';
 *    ...
 *    const factors = filterTotpFactors(response.data.response.results);
 * ```
 */
export const filterTotpFactors = (factors: any[]): MfaEnrollmentFactor[] => (
  (factors || []).filter((factor) => (
    factor?.factorType === TOTP_FACTOR_TYPE &&
    (factor?.provider === 'OKTA' || factor?.provider === 'GOOGLE')
  ))
);

/**
 * @function parseLoginResult
 *
 * The single, isolated discriminator for a `POST /login` response. It returns a
 * structured `LoginResult` identifying whether login is complete, the user must
 * enroll a factor, or the user must answer a challenge. The `Mfa-Status` response
 * header is the primary signal; the response body shape is a defensive fallback for
 * when the header is not exposed via CORS.
 *
 * ```typescript
 *    import {parseLoginResult} from '@ion/core/src/services/ConnectUtils';
 *    ...
 *    const result = parseLoginResult(response);
 * ```
 */
export const parseLoginResult = (resp: any): LoginResult => {
  const header    = resp?.headers?.['mfa-status'];
  const results   = resp?.data?.response?.results;
  const authToken = parse(resp, 'authToken') as unknown as string;

  let status = header;
  if (!status) {
    if (authToken) {
      status = '';
    } else if (results?.[0]?.stateToken && results?.[0]?.factorId) {
      status = MfaStatus.Required;
    } else if (results?.length) {
      status = MfaStatus.Enroll;
    }
  }

  if (status === MfaStatus.Enroll) {
    return {
      status            : 'enroll',
      intermediateToken : readIntermediateToken(resp),
      factors           : filterTotpFactors(results)
    };
  }

  if (status === MfaStatus.Required) {
    const challenge = results?.[0] || {};
    return {
      status            : 'challenge',
      intermediateToken : readIntermediateToken(resp),
      stateToken        : challenge.stateToken,
      factorId          : challenge.factorId,
      oktaUserId        : challenge.oktaUserId,
      expiresAt         : challenge.expiresAt
    };
  }

  return {
    status : 'authenticated',
    authToken
  };
};

/**
 * Provides a convenience function which parses a CTC collection down to an array
 * of entity objects. For example, CTC services return entities (such as a User)
 * as objects with all properties nested within the entity name (user.user) rather
 * than the top-level object, thus, user.user.id simply becomes user.id, via the
 * parseCollection utility.
 *
 * Note, this function is generally used by Axios based Services APIs, however,
 * it can also be used in the context of Fetch Based Service APIs as well.
 *
 * ```typescript
 *
 *    const response = await client.get(url(`/accounts/${id}/subaccounts`));
 *    return parseCollection(response, 'account'));
 *
 * ```
 */
export const parseCollection = (resp: any, key = 'result', requiredMetaData = false) => {
  let res = resp;
  if (requiredMetaData) {
    const {results, totalCount, _links} = resp.data?.response || {};
    res = {
      [key] : results?.map(result => result[key] || result),
      totalCount,
      _links
    };
  } else {
    const response = resp?.data ? resp?.data : resp;
    res  = response?.response?.results;
    if (res?.length && key !== '') {
      res = res.map(item => item[key]);
    }
  }
  return res;
};

/**
 * @function parseAssetsBulkCollection
 *
 * Provides a convenience function which parses a CTC collection down to an array
 * of Asset objects.
 *
 * ```typescript
 *
 *    const results = await client.post(url('/assets/bulk'),
 *      JSON.stringify(assets.map(asset => ({asset}))));
 *    return parseAssetsBulkCollection(response, 'account'));
 *
 * ```
 */
export const parseAssetsBulkCollection = (responses: any, entity: string = 'asset', items?: Asset[]) => {
  const errors = [];
  const resErrors = responses?.errors || responses?.data?.response?.errors;
  if (resErrors) {
    resErrors.forEach(err => {
      try {
        const error = safeJSONParse(err);
        if (error) {
          const {
            resource,
            reasons: rawReasons,
            message
          } = error;
          let asset = safeJSONParse(resource)?.[entity];
          const reasons = Object.values(rawReasons)?.toString();
          if (!asset && items && message) {
            // onlyNumbers returns a string despite its declared type, so the
            // id must be coerced to match against the numeric Asset id ...
            const assetId = onlyNumbers(message);
            assetId && (asset = items.find(({id}) => String(id) === assetId));
          }
          if (asset) {
            errors.push({
              error   : reasons,
              message : reasons,
              name    : asset.name,
              asset
            });
          }
        } else {
          errors.push(err);
        }
      } catch (e) {
        error('[CalAmp iOn Asset Import]: Unable to parse Asset Import Error');
      }
    });
  }
  return {
    results : parseCollection(responses, entity),
    errors
  };
};

/**
 * @setGlobalErrorHandler
 *
 * Allows for providing a global error handler for all connect requests.
 */
export const setConnectErrorHandler = (onError: (ServiceError) => void) => {
  errorInterceptor(client, onError);
};

/**
 * The setConnectConfig function allows for initializing the configuration
 * of the Connect Client.
 *
 * ```typescript
 *
 *    import {loadConfig} from '@ion/core/src/environment/environment';
 *    import {setConnectConfig} from '@ion/core/src/services/ConnectUtils';
 *
 *    loadConfig(
 *      config => {
 *        setConnectConfig(config);
 *        ...
 *      },
 *      ...
 *    );
 *
 * ```
 */
export const setConnectConfig = (connect: ConnectConfig) => {
  config = {
    ...connect,
    withCredentials : true,
    baseURL         : connect.serviceURL,
    timeout         : 60000,
    headers         : {
      'calamp-services-app' : connect.appKey,
      'Content-Type'        : 'application/json'
    }
  };
  client = createAsyncClient(config);
  return client;
};

/**
 * The util function to set Authorization Header in axios client
 *
 * @param token The token to set in header.
 */
export const setAuthorizationHeader = (token: string) => setClientAuthorizationHeader(client, token);

/**
 * @function constructSearchTerms
 *
 * Provides a utility which builds a CTC searchTerm request object
 * based on the provided terms argument. The terms argument is an
 * object where each key is the name of a searchable attribute of
 * a given CTC entity, and the value of each key is an object which
 * defines the value of the attribute, and an optional wildcard
 * option, which, when the value is a string, determines if the a
 * wildcard is to be performed for the given attribute, meaning,
 * any matches are to be returned which contain the given value.
 *
 * ```typescript
 *
 *   import {constructSearchTerms} from '@ion/core/src/environment/environment';
 *
 *   const terms = {
 *     name: {
 *       value: 'UI',
 *       wildcard: true
 *     },
 *     account: {
 *       value: {
 *         href: 'https://connect.calamp.com:443/connect/services/accounts/12',
 *         rel: 'account'
 *       }
 *     }
 *   };
 *
 *   const searchTerms = constructSearchTerms(terms);
 *   console.log(searchTerms.name);
 *   // "*$UI*"
 *
 *   console.log(searchTerms.account);
 *   //  value: {
 *   //    href: 'https://connect.calamp.com:443/connect/services/accounts/12',
 *   //    rel: 'account'
 *   //  }
 *
 * ```
 *
 * @param  {object} terms An object where each key is the name of a searchable
 *         attribute of a given CTC entity, and the value of each key is an
 *         object which defines the value of the attribute, and an optional
 *         wildcard option, which, when the value is a string, determines if
 *         the a wildcard is to be performed for the given attribute, meaning,
 *         any matches are to be returned which contain the given value.
 * @return {object} the constructed searchTerms object based on the provided
 *         terms object.
 */
export const constructSearchTerms = (terms: SearchTerm) => {
  const searchTerms = {};
  Object.entries(terms).forEach(([key, value]) => {
    searchTerms[key] = value?.wildcard ? `*${value?.value}*` : value?.value;
  });
  return searchTerms;
};

export const createAlertNotificationSearchTerms = ({
  includeSubAccount,
  accountId,
  filterOn,
  subAccountIds
}) => {
  const createdOn = isUndefined(filterOn?.createdOn) ? '[NOW-7DAYS TO NOW]' : convertModularSearchDateRange(filterOn?.createdOn);
 
  const searchTerms = {
    ...filterOn,
    createdOn
  };

  if (!includeSubAccount) {
    return {
      ...searchTerms,
      accountId
    };
  }
  
  if (subAccountIds && subAccountIds.length > 0) {
    return {
      ...searchTerms,
      accountId : subAccountIds
    };
  }
  
  return {
    ...searchTerms,
    accountId
  };
};

/**
 * @function createPartitionMap
 *
 * Provides a utility which allows for creating a mapping of a collection
 * of items by a given key name.
 *
 * ```typescript
 *
 *   import {createPartitionMap} from '@ion/core/src/services/ConnectUtils';
 *
 *   let items = [
 *     {name: 'Item A'},
 *     {name: 'Item B'},
 *     {name: 'Item C'}
 *   ];
 *
 *   const map = createPartitionMap(items, 'name');
 *   console.log(map['Item A'] === items[0]); // true
 *   console.log(map['Item B'] === items[1]); // true
 *   console.log(map['Item C'] === items[2]); // true
 *
 *   const map = createPartitionMap(items, 'name', true);
 *   console.log(map['Item A']); // true
 *   console.log(map['Item B']); // true
 *   console.log(map['Item C']); // true
 *   console.log(map['Fake Item']); // false
 *
 * ```
 * @param   {array} items An array of items which are to be mapped for
 *          partitioning.
 * @param   {string | array} key The entity attribute name(s) to be used to
 *          determine uniqueness when creating the partition map. Defaults
 *          to 'name'.
 * @param   {boolean} simple Whe true, indicates that the value for each
 *          entry in the partion map is to be a boolen; true if the item
 *          exists, false if not. This argument defaults to false, meaning
 *          that the entity itself will be used as the value. Defaults to
 *          false.
 * @returns {object} An object containing the partion mappings.
 */
export const createPartitionMap = (
  items: Array<GenericObject>,
  key: string | string[] = 'name',
  simple: boolean = false
) => {
  const map = {};
  items.forEach(item => {
    map[getUniqueKey(item, key)] = !simple ? item : true;
  });
  return map;
};

/**
 * @private
 * Used by the partitionBulkUpdates and createPartitionMap methods below.
 *
 * ```typescript
 *
 *   let item = {prop: {subProp: 'foo'}};
 *   let key = 'prop.subProp';
 *   console.log(getUniqueKey(item, key)); // 'foo'
 *
 *   let item = {prop: {subProp: 'foo'}, prop2: 'bar'};
 *   let key = 'prop2';
 *   console.log(getUniqueKey(item, key)); // 'bar'
 *   let key = ['prop.subProp', 'prop2'];
 *   console.log(getUniqueKey(item, key)); // 'foo-bar'
 *
 *   let item = {prop: {subProp: 'foo'}, prop2: 'bar', prop3: 'baz'};
 *   let key = ['prop2', 'prop3'];
 *   console.log(getUniqueKey(item, key)); // 'bar-baz'
 *
 * ```
 */
const getUniqueKey = (item, key) => isArray(key) ? key.map(k => getNestedProperty(item, k)).join('-') : item[key];

/**
 * @function partitionBulkUpdates
 *
 * The partitionBulkUpdates method provides a utility which allows for
 * partitioning items which can be updated and items which need to be
 * created.
 *
 * ```typescript
 *
 *   import {partitionBulkUpdates} from '@ion/core/src/services/ConnectUtils';
 *
 *   let uploaded = [
 *     {name: 'Item A'},
 *     {name: 'Item B'},
 *     {name: 'Item C'},
 *     {name: 'Item D [Changed Name]'}
 *   ];
 *   let searched = [
 *     {id: 0, name: 'Item A'},
 *     {id: 1, name: 'Item B'},
 *     {id: 2, name: 'Item C'}
 *   ];
 *
 *   // partition and merge uploaded items and matched search results ...
 *   const {updates, adds} = partitionBulkUpdates(uploaded, searched, 'asset', 'name');
 *
 *   console.log(updates.length); // 3
 *   console.log(adds.length); // 1
 *
 *   // merged uploaded items and search result matches ...
 *   const {updates} = partitionBulkUpdates(uploaded, searched, 'asset', 'name');
 *
 *   let asset = updates[0].asset;
 *   console.log(asset.id).to.equal(0);
 *   console.log(asset.name).to.equal('Item A');
 *
 *   asset = updates[1].asset;
 *   console.log(asset.id).to.equal(1);
 *   console.log(asset.name).to.equal('Item B');
 *
 *   asset = updates[2].asset;
 *   console.log(asset.id).to.equal(2);
 *   console.log(asset.name).to.equal('Item C');
 *
 * ```
 *
 * @param   {array} uploaded The items uploaded by the user which are to
 *          be updated.
 * @param   {array} searched The CTC search results for the uploaded items.
 * @param   {string | array} key The entity attribute name(s) to be used to
 *          determine uniqueness. Defaults to 'name'.
 * @returns {object} An object containing the partitioned results, each
 *          identified by the following keys:
 *          updates: an array of items which were found by the search results.
 *          This array of items is to be used for sending to a CTC bulk PUT
 *          operation (update the items).
 *          adds: an array of items which were not found by the search results.
 *          This array of items is to be used for sending to a CTC bulk POST
 *          operation (create the items).
 */
export const partitionBulkUpdates = ({
  uploaded,
  searched,
  entity,
  key = 'name',
  excludeAttrs = false,
  validator = null
}: PartitionBulkUpdates) => {
  const uploadedMap = createPartitionMap(uploaded, key);
  const searchedMap = createPartitionMap(searched, key);
  const partitioned = partition(uploaded, item => !isUndefined(searchedMap[getUniqueKey(item, key)]));
  const resolver    = validator || propertyResolver;
  const update = (existing, updated) => {
    Object.keys(updated).forEach(attr => {
      if (attr !== 'extendedAttribute' || !excludeAttrs) {
        const condition = resolver(existing, updated, attr);
        if (!condition) {
          existing[attr] = updated[attr];
        }
      }
      if (attr === 'newName' && !isNil(updated[attr])) {
        existing.name = updated[attr];
      }
    });
    return existing;
  };
  return {
    updates : partitioned[0].map(item => {
      const unique = getUniqueKey(item, key);
      return {
        [entity] : update(searchedMap[unique], uploadedMap[unique])
      };
    }),
    adds : (partitioned.length === 2 ? partitioned[1] : []).map(item => {
      return {
        [entity] : item
      };
    })
  };
};

/**
 * Walks the specified nested object according to the specified property string
 * (ex. 'a.b.c') and returns the property value.
 * @param {object} obj A nested object.
 * @param {string} prop String describing the property to return.
 */
export const getNestedProperty = (obj, prop) => {
  const [first, ...rest] = prop.split('.');
  return rest.length ? getNestedProperty(obj[first], rest.join('.')) : obj[first];
};

/**
 * @function propertyResolver
 *
 * Given an existing entity, and an updated entity, updates the given
 * attribute on the existing entity to the value of the updated entity
 * if provided, otherwise, the existing value is preserved. This utility
 * is generally used for entity imports in order to ensure existing
 * values are not overwritten by blank fields not required by the import.
 *
 * @param  {object} existing A CTC entity, such as an Asset, Group, etc.
 * @param  {object} updated An updated entity, typically via a spreadsheet import.
 * @param  {string} attr The entity attribute to be updated.
 */
export const propertyResolver = (existing, updated, attr) => {
  if (existing && updated && attr) {
    const updatedValue = updated[attr];
    if (attr === 'operators' && updated.forceOperatorsClear) {
      existing[attr] = updatedValue;
      return true;
    }
    if (!isNil(updatedValue)) {
      if (!isArray(updatedValue) || updatedValue.length) {
        existing[attr] = updatedValue;
      }
    }
  }
  return true;
};

/**
 * @function propertyResolver
 *
 * The aggregateResults function provides a means of aggregating multiple
 * result sets specifically for use with CTC Bulk Updates whereby a bulk
 * update operation may require multiple calls to perform the update, or
 * require both Bulk add and Bulk Update calls to be made in order to
 * perform a given Bulk Entity Update.
 *
 * ```typescript
 *
 *   import {aggregateResults} from '@ion/core/src/services/ConnectUtils';
 *
 *   // current results and errors ...
 *   let current = {
 *     results : ['A', 'B', 'C'],
 *     errors  : ['D']
 *   };
 *   // latest results and errors ...
 *   let response = {
 *     results : ['X', 'Y', 'Z'],
 *     errors  : ['E', 'F', 'G']
 *   };
 *
 *   const {results, errors} = aggregateResults(current, response);
 *
 *   console.log(results.length); // 6
 *   console.log(errors.length);  // 4
 *   console.log(results[0]); // 'A';
 *   console.log(results[1]); // 'B';
 *   console.log(results[2]); // 'C';
 *   console.log(results[3]); // 'X';
 *   console.log(results[4]); // 'Y';
 *   console.log(results[5]); // 'Z';
 *
 *   console.log(errors[0]); // 'D';
 *   console.log(errors[1]); // 'E';
 *   console.log(errors[2]); // 'F';
 *   console.log(errors[3]); // 'G';
 *
 * ```
 * @param   {object} current An object containing the existing aggregated
 *          results, which consists of two properties; results, and errors.
 *          The results property is an array of aggregated Bulk operation
 *          successful results, and the errors property is an Array of
 *          aggregated Bulk operation failed updates.
 * @param   {object} results An object which consists of two properties;
 *          results, and errors, whereby the results property is an array
 *          Bulk operation successful results, and the errors property is
 *          an Array of Bulk operation failed results. The results and the
 *          errors array from this object are concated to the results array
 *          and the errors array of the current argument.
 * @returns {object} A new object containing the given response results
 *          and errors concated to the provides current.results Array and
 *          current.errors Array.
 */
export const aggregateResults = (current: GenericObject, response = {results: [], errors: []}) => {
  return {
    results : current.results.concat(response.results),
    errors  : current.errors.concat(response.errors)
  };
};

/**
 * Helps to create extended attributes for any entity ...
 *
 * ```typescript
 *
 * import {createExtendedAttributes} from '@ion/core/src/services/ConnectUtils';
 *
 * const extendedAttributes = createExtendedAttributes(entity, updated, application);
 *
 * ```
 *
 * @param entity object where extended attricute is to be created
 * @param updated the current entity
 * @param application the application object
 */
export const createExtendedAttributes = (entity, updated, application) => {
  let extAttrValue = updated;
  if (entity?.extendedAttribute) {
    const {extendedAttribute} = entity;
    if (extendedAttribute?.value) {
      const values = safeJSONParse(extendedAttribute?.value);
      if (values && isObject(values) && !isEmpty(values)) {
        Object.keys(updated)?.forEach(key => {
          values[key] = updated[key];
        });
        extAttrValue = values;
      }
    }
  }
  if (extAttrValue) {
    return {
      application,
      value : JSON.stringify(extAttrValue)
    };
  }
  return extAttrValue;
};

/**
 * @function addApplication
 *
 * Given an account and an application, inspects the account's applications
 * array and adds the application to the account's applications if it does
 * not exist within the array. Likewise, if the account does not contain
 * any applications, the application will be added to the account's applications.
 *
 * ```typescript
 *
 * import {addApplication} from '@ion/core/src/services/ConnectUtils';
 *
 * addApplication(account, application);
 *
 * ```
 *
 * @param   {account} account The CTC Assets entities which are to be updated.
 * @param application the application object
 */
export const addApplication = (account, application) => {
  const {applications} = account;
  if (applications?.length) {
    const {href} = application;
    const hasApp = applications.some(app => app.href === href);
    if (!hasApp) {
      account.applications.push(application);
    }
  } else {
    account.applications = [application];
  }
};

/**
 * @function setAccountExtendedAttributes
 *
 * Given a list of accounts, updates each account with CTC extendedAttributes
 * based on the presence of Asset, Operator, and / or Route categories.
 *
 * ```typescript
 *
 * import {setAccountExtendedAttributes} from '@ion/core/src/services/ConnectUtils';
 *
 * setAccountExtendedAttributes(account, application);
 *
 * ```
 *
 * @param {accounts} accounts The CTC Assets entities which are to be updated.
 * @param application the application object
 */
export const setAccountExtendedAttributes = (accounts: ImportAccount[], application: Link) => {
  accounts.forEach(item => {
    const account = (item.account as ImportAccount) || item;
    const {href, rel} = application;
    addApplication(account, application);
    account.autoAssetAssociationEnabled = account?.autoAssetAssociationEnabled || false;

    const {
      assetCategories,
      operatorCategories,
      routeCategories,
      landmarkCategories,
      extendedAttribute,
      overspeedThreshold,
      workStopDuration,
      timeFormat,
      dateFormat
    } = account;
    if (assetCategories ||
      operatorCategories ||
      routeCategories ||
      landmarkCategories ||
      overspeedThreshold ||
      workStopDuration ||
      timeFormat ||
      dateFormat
    ) {
      const values: any = {};
      if (assetCategories) {
        values.assetCategories = fromCSV(assetCategories);
        delete account.assetCategories;
      }
      if (operatorCategories) {
        values.operatorCategories = fromCSV(operatorCategories);
        delete account.operatorCategories;
      }
      if (routeCategories) {
        values.routeCategories = fromCSV(routeCategories);
        delete account.routeCategories;
      }
      if (landmarkCategories) {
        values.landmarkCategories = fromCSV(landmarkCategories);
        delete account.landmarkCategories;
      }
      if (overspeedThreshold) {
        values.overspeedThreshold = fromCSV(overspeedThreshold.toString()).toString();
        delete account.overspeedThreshold;
      }
      if (workStopDuration) {
        values.workStopDuration = fromCSV(workStopDuration.toString()).toString();
        delete account.workStopDuration;
      }
      if (timeFormat) {
        values.timeFormat = timeFormat;
        delete account.timeFormat;
      }
      if (dateFormat) {
        values.dateFormat = dateFormat;
        delete account.dateFormat;
      }
      if (Object.keys(values).length) {
        const value = extendedAttribute ? parseExtendedAttributes(account, true) : values;
        if (extendedAttribute) {
          value.assetCategories     = values.assetCategories || value.assetCategories;
          value.operatorCategories  = values.operatorCategories || value.operatorCategories;
          value.routeCategories     = values.routeCategories || value.routeCategories;
          value.landmarkCategories  = values.landmarkCategories || value.landmarkCategories;
          value.overspeedThreshold  = values.overspeedThreshold || value.overspeedThreshold;
          value.workStopDuration    = values.workStopDuration || value.workStopDuration;
          value.timeFormat          = values.timeFormat || value.timeFormat;
          value.dateFormat          = values.dateFormat || value.dateFormat;

          !value.assetCategories && delete value.assetCategories;
          !value.operatorCategories && delete value.operatorCategories;
          !value.routeCategories && delete value.routeCategories;
          !value.landmarkCategories && delete value.landmarkCategories;
          !value.overspeedThreshold && delete value.overspeedThreshold;
          !value.workStopDuration && delete value.workStopDuration;
          !value.timeFormat && delete value.timeFormat;
          !value.dateFormat && delete value.dateFormat;
        }
        account.extendedAttribute = {
          application : {href, rel},
          value       : JSON.stringify(value)
        };
      }
    }
  });
};

/**
 * @function mergeAccounts
 *
 * Given a list of complete CTC Account Entity representations, and a list of
 * import Asset representations, merges the values of the imported accounts to
 * the CTC accounts entities and returns the results.
 *
 * ```typescript
 *
 * import {mergeAccounts} from '@ion/core/src/services/ConnectUtils';
 *
 * mergeAccounts(account, application);
 *
 * ```
 *
 * @param {array} entities An array of entities to be merged with imported entities
 * @param {array} imported An array of imported entities
 */
export const mergeAccounts = (entities: ImportAccount[], imported: ImportAccount[]) => {
  const mappings = {};
  imported.forEach(account => {
    const {
      assetCategories,
      routeCategories,
      operatorCategories,
      landmarkCategories,
      overspeedThreshold,
      workStopDuration,
      timeFormat,
      dateFormat
    } = account;
    if (assetCategories ||
      routeCategories ||
      operatorCategories ||
      landmarkCategories ||
      overspeedThreshold ||
      workStopDuration ||
      timeFormat ||
      dateFormat
    ) {
      mappings[account.name] = {
        assetCategories,
        routeCategories,
        operatorCategories,
        landmarkCategories,
        overspeedThreshold,
        workStopDuration,
        timeFormat,
        dateFormat
      };
    }
  });
  entities.forEach((account) => {
    const categories = mappings[account.name];
    if (categories) {
      const {
        assetCategories,
        routeCategories,
        operatorCategories,
        landmarkCategories,
        overspeedThreshold,
        workStopDuration,
        timeFormat,
        dateFormat
      } = categories;
      account.assetCategories    = assetCategories;
      account.routeCategories    = routeCategories;
      account.operatorCategories = operatorCategories;
      account.landmarkCategories = landmarkCategories;
      account.overspeedThreshold = overspeedThreshold;
      account.workStopDuration = workStopDuration;
      account.timeFormat = timeFormat;
      account.dateFormat = dateFormat;
    }
  });
  return entities;
};

/**
 * Helper function to return the JSON formatted search terms needed for the given search type.
 * The "size" search term specifies the upper bound of the number of results to be returned
 * (default value = 10).
 */
export const MAX_AGGREGATION_RESULTS_APP_ID = 1000;

export const getAggregationsFromSearchType = (deviceSearchType: DeviceSearchTypes,
  returnObject: boolean = false): string | object => {
  switch (deviceSearchType) {
    case DeviceSearchTypes.FIRMWARE_VERSION:
      return safeJSONStringify({
        firmwareVersion : {terms: {field: 'extensions.firmwareVersion'}}
      });
    case DeviceSearchTypes.CONFIGURATION_VERSION:
      return safeJSONStringify({
        configVersion : {terms: {field: 'extensions.configVersion'}}
      });
    case DeviceSearchTypes.DEVICE_MODEL:
      return safeJSONStringify({
        deviceModel    : {terms: {field: 'typeName'}},
        deviceActivity : {terms: {field: 'status'}}
      });
    case DeviceSearchTypes.APP_ID: {
      const appId_aggs = {
        appId : {terms: {field: 'extensions.appId', size: MAX_AGGREGATION_RESULTS_APP_ID}}
      };
      return returnObject ? appId_aggs : safeJSONStringify(appId_aggs);
    }
    default:
      return null;
  }
};

/**
 * Helper "enum" to map communication search types to their respective day periods.
 */
const DeviceCommunicationPeriods = {
  [DeviceCommunicationSearchTypes.LAST_7_DAYS]  : 7,
  [DeviceCommunicationSearchTypes.LAST_14_DAYS] : 14,
  [DeviceCommunicationSearchTypes.LAST_30_DAYS] : 30,
  [DeviceCommunicationSearchTypes.LAST_90_DAYS] : 90
};

/**
 * Based on the provided device communication search type, this will return the (formatted for the api) starting string
 * datetime of when to retrieve device communication from.
 */
export const getDateTimeForSearchType = (
  searchType: DeviceCommunicationSearchTypes = DeviceCommunicationSearchTypes.LAST_7_DAYS
): string => {
  const {fromDate} = getDaysFromToday(DeviceCommunicationPeriods[searchType], 'subtract', {stringify: false});
  const formattedDate = (fromDate as Moment).format('YYYY-MM-DDTHH\\:mm\\:ss.000[Z]');
  const parsedDate = formattedDate.replace(/:/g, '\\:');
  return parsedDate;
};

/**
 * The utility function helps to  construct the devices map based on group. The map contains group
 * as a key and the list(array) of devices as a value for the group key. The function takes devices
 * input and give a mapped device map as a output.
 *
 * ```typescript
 *
 *  import {mapDeviceSearchResults} from '@ion/core/src/services/ConnectUtils';
 *
 *  const resultMap = mapDeviceSearchResults(devices);
 *
 * ```
 */
export const mapDeviceSearchResults = (devices: CTCEntity[], mappedDevices = {}) => {
  devices?.forEach(device => {
    if (Array.isArray(device)) {
      mapDeviceSearchResults(device, mappedDevices);
    } else {
      const key = entityId(device.account);
      if (isArray(mappedDevices[key])) {
        mappedDevices[key].push(device);
      } else {
        mappedDevices[key] = [device];
      }
    }
  });
  return mappedDevices;
};

/**
 * The parseDownloadJobs utility handles to map items
 *
 * ```typescript
 *
 * import {parseDownloadJobs} from '@ion/core/src/services/ConnectUtils';
 *
 * const results = parseDownloadJobs(tasks);
 *
 * ```
 */
export const parseDownloadJobs = (tasks: any) => {
  const statusInProgress = translate('common:jobStatus.IN_PROGRESS');
  if (!isNull(tasks?.tasks)) {
    return tasks?.tasks?.map?.((item, index) => {
      const {task} = item;
      const {counts = {}, metaData, taskType, progressStatus} = task || {};
      const isInProgress = progressStatus === DownloadJobType.Inprogress;
      return {
        ...item,
        ...counts,
        taskOrder      : index + 1,
        task           : taskType,
        description    : metaData?.title,
        total          : counts?.total ?? '0',
        completed      : counts?.completed ?? '0',
        pending        : counts?.pending ?? '0',
        failed         : counts?.failed ?? '0',
        cancelled      : counts?.cancelled ?? '0',
        progressStatus : isInProgress ? statusInProgress : progressStatus
      };
    });
  } else {
    return [];
  }
};

/**
 * The parseDevice utility handles to map items
 *
 * ```typescript
 *
 * import {parseDevice} from '@ion/core/src/services/ConnectUtils';
 *
 * const results = parseDevice(items);
 *
 * ```
 */
export const parseDevices = (items) => {
  const {
    statCode,
    diagCode,
    diagCode32Bit
  } = getCodeMappings();

  return items?.map(items => {
    const {
      device,
      taskStatus,
      identifier,
      error
    } = items || {};

    const {errorCodes} = taskStatus || {};
    const errorCode    = errorCodes?.[0];
    if (!isUndefined(error)) {
      return {
        ...items,
        esn        : identifier,
        errorCodes : error
      };
    }
    return {
      ...items,
      esn          : device?.esn,
      serialNumber : device?.serialNumber,
      typeName     : device?.type?.title,
      errorCodes   : hasDiagCode(errorCode) ? getErrorCode({
        error : errorCode,
        appId : device?.extensions?.appId,
        statCode,
        diagCode,
        diagCode32Bit
      }) : errorCode,
      status     : taskStatus?.status,
      updateTime : taskStatus?.updateTime
    };
  });
};

export const getNoCacheHeaders = (type) => {
  const config  = {
    headers : {
      'Cache-Control'               : 'no-cache',
      'Content-Type'                : type ? 'application/json' : 'text/plain',
      'calamp-services-app'         : get('appKey'),
      'Access-Control-Allow-Origin' : '*'
    }
  };
  return config;
};

/**
 * The getAWSFileUploadHeader utility returns a list of form data.
 *
 * ```typescript
 *
 * import {getAWSFileUploadHeader} from '@ion/core/src/services/ConnectUtils';
 *
 * const header = getAWSFileUploadHeader(field, file);
 *
 * ```
 */
export const getAWSFileUploadHeader = (field, file) => {
  const formData              = new FormData();
  const {key, bucket, Policy} = field;
  formData.append('key', key);
  formData.append('bucket', bucket);
  formData.append('X-Amz-Algorithm', field['X-Amz-Algorithm']);
  formData.append('X-Amz-Credential', field['X-Amz-Credential']);
  formData.append('X-Amz-Date', field['X-Amz-Date']);
  formData.append('Policy', Policy);
  formData.append('X-Amz-Signature', field['X-Amz-Signature']);
  formData.append('file', file);
  formData.append('Content-Type', 'multipart/form-data');
  return formData;
};

/**
 * The getCarrierDeviceIdentifierTypes utility returns a list of supported CTC
 * Carrier Device Identifier types.
 *
 * ```typescript
 *
 * import {getCarrierDeviceIdentifierTypes} from '@ion/core/src/services/ConnectUtils';
 *
 * const types = getCarrierDeviceIdentifierTypes();
 *
 * ```
 */
export const getCarrierDeviceIdentifierTypes = (): CarrierDeviceIdentifierType[] => {
  return Object.values(CarrierDeviceIdentifierType) as CarrierDeviceIdentifierType[];
};

/**
 * Provides a utility which determines query string needed for fetching sci devices.
 */
export const getQueryStringForBulkSCI = (deviceTypes, searialNumbers?: string[]): string => {
  if (deviceTypes && !isEmpty(searialNumbers)) {
    return `${deviceTypes?.idType}: (${searialNumbers.join(' OR ')}) AND typeName: ${deviceTypes?.deviceType}`;
  }
  return `${deviceTypes?.searchQuery} AND typeName: (${deviceTypes?.deviceType?.join(' OR ')})`;
};

/**
 * Provides a utility to create axios client.
 */
export const createClient = (defaultParams) => {
  const client = createAsyncClient(config, false);
  client.defaults = defaultParams;
  return client;
};

/**
 * Util function to set/reset authorization header to axios clients
 *
 * @param client the axios instance.
 * @param authHeader the authorization header value.
 */
export const setClientAuthorizationHeader = (client: AxiosInstance, authHeader: string) => {
  if (authHeader) {
    client && (client.defaults.headers.common.Authorization = authHeader);
  } else {
    client && delete client.defaults.headers.common.Authorization;
  }
};

/**
 * Provides a utility which determines query string needed for fetching devices.
 */
export const getQueryStringForDevices = (identifiers: string[]): string => {
  return `${identifiers?.join(' OR ')}`;
};

/**
 * Creates the endpoint URL for loading bulk entities for a given main task.
 *
 * @param id the id of the main task to retrieve bulk entities for
 * @param cursor the encrypted id of the last retrieved subtask for seamless pagination
 * @param include flag signifying whether or not the bulk entities should be returned, should be true
 * @returns string URL
 */
export const createLoadBulkEntitiesURL = (id: string, cursor = '', include = true): string => {
  const cursorParam = getCursorQueryParam(cursor);
  return url(`/tasks/${id}/subtasks?bulkEntities=${include}${cursorParam}`, Service.ApiBaseUrl);
};

/**
 * Creates the endpoint URL for loading task statuses (subtasks) of a main task.
 *
 * @param id the id of the main task to retrieve task statuses for
 * @param cursor the encrypted id of the last retrieved subtask for seamless pagination
 * @returns string URL
 */
export const createLoadTaskStatusURL = (id: string, cursor = ''): string => {
  const cursorParam = getCursorQueryParam(cursor);
  return url(`/tasks/${id}/subtasks?expand=info&bulkOperations=true&limit=10${cursorParam}`, Service.ApiBaseUrl);
};

/**
 * Creates the endpoint URL for loading subtasks of a main task.
 *
 * @param id the id of the main task to retrieve subtasks for
 * @param cursor the encrypted id of the last retrieved subtask for seamless pagination
 * @param isBulkOperation flag signifying if the loaded subtasks are for a bulk operation
 * @param isBulkExport flag signifying if the loaded subtasks are for a bulk export operation
 * @returns string URL
 */
export const createLoadSubtasksURL = (
  id: string,
  cursor = '',
  isBulkOperation = true
): string => {
  let queryParams = '';
  const cursorParam = getCursorQueryParam(cursor);
  if (isBulkOperation) {
    // keeping the payload under 1 MB
    const limit = 1;
    queryParams = `?expand=info&bulkOperations=${isBulkOperation}&limit=${limit}${cursorParam}`;
  } else {
    queryParams = `?bulkEntities=true&expand=info${cursorParam}`;
  }
  return url(`/tasks/${id}/subtasks${queryParams}`, Service.ApiBaseUrl);
};

/**
 * Given a cursor, determines the cursor param.
 *
 * @param cursor the cursor to create the query param for
 * @returns a valid cursor query param if present, otherwise empty string (no param)
 */
const getCursorQueryParam = (cursor: string): string => (
  cursor ? `&cursor=${cursor}` : ''
);

/**
 * Splits an array into smaller chunks of a specified size.
 */
export const chunkRequests = <T>(array: T[], chunkSize: number): T[][] => {
  return Array.from({length: Math.ceil(array.length / chunkSize)}, (_, index) => (
    array.slice(index * chunkSize, (index + 1) * chunkSize)
  ));
};

export enum MANUFACTURING_AUX_VALUES {
  CARRIER = 'CARRIER'
}

/**
 * Creates a URL pointing to the common manufacturing service.
 *
 * @param resource the remaining resource url to append to the mfg URL
 * @returns the composed manufacturing endpoint URL at the given resource
 */
export const createManufacturingURL = (resource: string): string => (
  url(`/mfg/v1/${resource}`, Service.ApiBaseUrl)
);

/**
 * Creates a URL pointing to the vehicle profile enum table.
 *
 * @param resource the remaining resource url to append to the vehicleInfo URL
 * @returns the composed vehicleInfo endpoint URL at the given resource
 */
export const vehicleProfileEnumURL = (resource: string): string => (
  url(`/vehicleInfo/enumTable?table=${resource}`, Service.VehicleServiceUrl)
);

/**
 * Util function to return endpoint and search.
 */
export const createLoadDeviceFilesRequest = ({
  accountId,
  sortOn,
  status,
  pageId = 1,
  pageSize = 20,
  apiVersion = 2.1,
  filterOn = {},
  isResults = false,
  includeSubGroups = false,
  includeAccountFilter = true,
  includeOnlyThisAccount = false,
  suppressNullVersions = false
}: LoadDeviceFilesParams) => {
  const onlyThisAccount = includeOnlyThisAccount ? '&exclude=inheritable' : '';
  const excludeNulls    = (
    suppressNullVersions && sortOn?.toString().includes('validationMetaData.fields.version') ? '&suppressNullandEmptyVersion=true' : ''
  );
  const uri           = `/files/search/?v=${apiVersion}&pg=${pageId}&pgsize=${pageSize}${onlyThisAccount}${excludeNulls}`;
  const endpoint      = url(setHierLevel(uri, includeSubGroups), Service.PulsBaseUrl);
  const accountFilter = includeAccountFilter ? {account: accountId} : {};
  const search        = isResults ? {validationStatus: '{\"status\":\"VALID\"}'} : {...filterOn};
  const searchTerm    = {
    search : {
      maxResults  : isResults ? MAX_RESULTS : pageSize,
      sort        : sortOn,
      searchTerms : {
        ...accountFilter,
        ...search,
        status
      }
    }
  };
  return {
    endpoint,
    search : searchTerm.search
  };
};

/**
 * the utility which returns the array of strings by spliting values of provided field
 *
 * @param filters the array of filters with different fields
 * @param searchField the field for which value should be splitted
 *
 * @returns the array of strings by spliting values
 */
export const getFilterValue =
  (filters: {field: string, keyword: string}[], searchField: string): string | string[] | [] => {
    const fieldValue = filters?.filter(({field}) => field === searchField)?.[0]?.keyword;
    return fieldValue ? processDelimiters(fieldValue, {splitOnly: true}) : [];
  };

/**
 * The method setMappedCategorySubType will check the Device Type / File Type result and fetching the 
 * category and subType from FileTypes -> fileMetaType object and pushing to an array with key value pair 
 * of name and platformId and create a new key called mappedCatSubType object and assign the above key value 
 * pairs into this object and insert it under deviceType object and return the entire deviceType object here.
 */
export const setMappedCategorySubType = (deviceFileTypes: PlatformDeviceTypes) => {
  return deviceFileTypes?.map((deviceType: GenericObject) => {
    const platFormIdList = [];
    deviceType?.fileTypes.map((fileType: PlatformFileType) => {
      const {fileMetaType, platformId} = fileType || {};
      const {category, subType}        = fileMetaType;
      const key                        = category.concat(subType);
      platFormIdList.push({name: key, platformId: platformId});
    });
    deviceType.mappedCatSubType = platFormIdList;
    return deviceType;
  });
};

/**
 * getMapSubTypeCategory method will return the mapped category and sub type 
 * from the device types.
 */
export const getMappedSubTypeCategory = (deviceTypes: PlatformDeviceTypes) => {
  const platFormIds = [];
  deviceTypes?.forEach(({mappedCatSubType}) =>
    mappedCatSubType?.forEach((item: GenericObject) => {
      platFormIds.push(item);
    }));
  
  return platFormIds?.reduce((acc, item) => {
    if (!acc[item.name]) {
      acc[item.name] = [];
    }
    if (!acc[item.name].some(p => p.platformId === item.platformId)) {
      acc[item.name].push(item?.platformId);
    }
    return acc;
  }, {});
};

/**
 * getSearchAfter method returns the searchAfter value from links object's next URL
 */
export const getSearchAfter = (links: {next?: string}): string => {
  const nextUrl = links?.next;
  if (!nextUrl) return '';
  const url1  = decodeURIComponent(nextUrl);
  const query = url1?.split('?')[1];
  const url   = new URLSearchParams(decodeURIComponent(query));
  return url?.get('searchAfter') ?? '';
};

export const createJobSearchV3Params = (accountId, query, useElasticQuery) => {
  let standardQuery = {
    query : {
      query_string : {
        query
      }
    }
  };
  if (useElasticQuery) {
    standardQuery = {
      query
    };
  }
  return JSON.stringify({
    account : {
      accountId          : accountId,
      includeSubaccounts : true
    },
    ...standardQuery,
    sort : [{
      propertyName : 'jobId',
      order        : 'DESC'
    }]
  });
};

export const createJobSearchQuery = ({
  query,
  fileIds,
  filters,
  dateRange,
  nameFilter,
  platformId,
  isFileTypeChecked
}) => {
  const jobNames               = !isEmpty(nameFilter) ? nameFilter?.split(',') : [];
  const shouldUseElasticSearch = (!!dateRange && dateRange.gte && dateRange.lte);
  const jobIds                 = getFilterValue(filters, 'jobId');
  const isMaxIds               = jobIds?.length > 20;
  const statusFilter           = getFilterValue(filters, 'progressStatus');
  const usernameFilter         = getFilterValue(filters, 'username');
  const hasValidPlatformId     = !isNil(platformId) && !isEmpty(platformId);
  const platformIdFilter       = hasValidPlatformId && isFileTypeChecked;
  const normalizedQuery        = jobIds?.length > 1? normalizeClauseList(query, {field: 'jobId', operator: 'OR'}) : query;
  
  const wildcardSearch = jobNames?.length ? {
    bool : {
      should : jobNames?.map((jobName) => (
        {
          wildcard : {
            name : {
              value            : `*${jobName}*`,
              case_insensitive : true
            }
          }
        }
      )),
      minimum_should_match : 1
    }
  } : null;
  
  const filesSearchQuery = {
    bool : {
      must : [{
        nested : {
          path  : 'tasks',
          query : {
            bool : {
              must : [
                {
                  terms : {
                    'tasks.metadata.file' : fileIds
                  }
                }
              ]
            }
          }
        }
      }]
    }
  };
  const nestedSearch = {
    nested : {
      path  : 'tasks',
      query : {
        bool : {
          must : [{
            term : {
              'tasks.metadata.platformId' : platformId
            }
          }]
        }
      }
    }
  };
  
  const elasticQuery = [{
    query_string : {
      query : normalizedQuery
    }
  }];
  
  if (jobNames?.length) {
    elasticQuery.push({...wildcardSearch});
  }
  if (fileIds?.length) {
    elasticQuery.push({...filesSearchQuery});
  }
  if (platformIdFilter) {
    elasticQuery.push({...nestedSearch});
  }
  
  const elasticSearch = {
    bool : {
      must   : elasticQuery,
      filter : shouldUseElasticSearch ? [
        {
          range : {
            createdOn : {
              gte : dateRange.gte,
              lte : dateRange.lte
            }
          }
        }
      ] : []
    }
  };
  
  const standardSearch = {
    query_string : {
      query : normalizedQuery
    }
  };
  
  const termsMustQuery = [];
  
  if (jobIds?.length) {
    termsMustQuery.push({
      terms : {
        jobId : jobIds
      }
    });
  }
  if (statusFilter?.length) {
    termsMustQuery.push({
      terms : {
        progressStatus : statusFilter
      }
    });
  }
  if (usernameFilter?.length) {
    termsMustQuery.push({
      terms : {
        username : usernameFilter
      }
    });
  }
  
  if (jobNames?.length) {
    termsMustQuery.push({
      bool : {
        should : jobNames?.map((jobName) => (
          {
            wildcard : {
              name : {
                value            : `*${jobName}*`,
                case_insensitive : true
              }
            }
          }
        )),
        minimum_should_match : 1
      }
    });
  };
  
  if (fileIds?.length) {
    termsMustQuery.push(filesSearchQuery);
  }
  
  const termsSearch = {
    bool : {
      filter : shouldUseElasticSearch ? [
        {
          range : {
            createdOn : {
              gte : dateRange.gte,
              lte : dateRange.lte
            }
          }
        }
      ] : [],
      must : termsMustQuery
    }
  };
  
  if (fileIds?.length && query && !isEmpty(query) && query !== '(jobId:*)') {
    filesSearchQuery?.bool?.must.push({
      query_string : {
        query : normalizedQuery
      }
    });
  }
  return isMaxIds
    ? termsSearch
    : shouldUseElasticSearch || platformIdFilter || !!(jobNames?.length)
      ? elasticSearch
      : fileIds?.length ? filesSearchQuery : standardSearch;
};

/** Flattens nested parameter values into a single-level object using
* dot-separated keys (e.g. `{ command: { field: value } }` becomes
* `{ "command.field": value }`).
* 
* Useful when parameter values need to be serialized, stored, or
* processed as a flat key-value map. A command holding a plain value
* rather than a nested field object is a standalone parameter - it maps
* back to a flat `command` key with no field suffix.
*/
export const fromNestedParameterValues = (
  values: Record<string, Record<string, number | boolean | string> | number | boolean | string>
): Record<string, number | boolean | string> =>
  Object.fromEntries(
    Object.entries(values).flatMap(([command, fields]) => (
      typeof fields === 'object'
        ? Object.entries(fields).map(([field, value]) => [`${command}.${field}`, value])
        : [[command, fields]]
    ))
  );

/*** Reconstructs nested parameter values from
* `{ "command.field": value }` to `{ command: { field: value } }`. A key
* with no `.` is a standalone parameter - its command has no field, so the
* value is assigned directly to the command rather than a nested object.
*/
export const toNestedParameterValues = (
  values: Record<string, number | boolean | string>
): Record<string, Record<string, number | boolean | string> | number | boolean | string> => (
  Object.entries(values).reduce((acc, [path, value]) => {
    const [command, field] = path.split('.');
    acc[command] = field === undefined
      ? value
      : {...(acc[command] as Record<string, number | boolean | string> ?? {}), [field]: value};
    return acc;
  }, {} as Record<string, Record<string, number | boolean | string> | number | boolean | string>)
);
